import * as React from "react";
import {
    Player,
    UIPlayerConfiguration,
} from "theoplayer/THEOplayer.chromeless";
import TrimmingArea from "./TrimmingArea";
import ResizeArea from "./ResizeArea";
import DurationBarV2 from "./DurationBarV2";
import { Crop, AspectRatio, PlayerButtonType } from "core/models";
import { Spinner, PlayerButtons } from "ui/components";
import formatDuration from "utils/formatDuration";
import normalizeCurrentTime from "utils/normalizeCurrentTime";
import { cx } from "utils/join-classnames";
import Configuration from "Configuration";
import $ from "./styles.module.scss";

interface Props {
    autoplay?: boolean;
    videoUrl: string;
    thumbnailUrl: string;
    initialOffsetStart: number;
    initialOffsetEnd: number;
    trimMode?: AspectRatio;
    sizeMode?: "crop" | "resize";
    onChange(start: number, end: number, crop: Crop): void;
    blur?: boolean;
}
interface Dimensions {
    width: number;
    height: number;
}

const DEFAULT_FRAME_RATE = 25;

const createUserId = () => Math.ceil(Math.random() * 1e6);

const TrimPlayerV2: React.FC<Props> = ({
    autoplay,
    videoUrl,
    thumbnailUrl,
    initialOffsetStart,
    initialOffsetEnd,
    trimMode,
    sizeMode,
    onChange,
    blur,
}) => {
    const [wasPlaying, setWasPlaying] = React.useState<boolean>(false);
    const [playing, setPlaying] = React.useState<boolean>(false);
    const [crop, setCrop] = React.useState<Crop>(new Crop());
    const [userInputId, setUserInputId] =
        React.useState<number>(createUserId());
    const [videoDuration, setVideoDuration] = React.useState<number>(0);
    const [currentTime, setCurrentTime] = React.useState<number>(0);
    const [videoSize, setVideoSize] = React.useState<Dimensions>({
        width: 1920,
        height: 1080,
    });
    const [offsetStart, setOffsetStart] =
        React.useState<number>(initialOffsetStart);
    const [offsetEnd, setOffsetEnd] = React.useState<number>(initialOffsetEnd);

    const videoRef = React.useRef<Player | null>(null);
    const videoBlurRef = React.useRef<Player | null>(null);
    const playerContainerRef = React.useRef<HTMLDivElement | null>(null);
    const playerContainerBlurRef = React.useRef<HTMLDivElement | null>(null);

    const animationFrame = React.useRef<number | null>(null);

    const [dynamicVideoStyle, setDynamicVideoStyle] =
        React.useState<React.CSSProperties>({});

    let dynamicDurationStyle = {};

    const THEO_LICENSE_KEY = Configuration.get("theoLicenseKey");

    // Player
    React.useEffect(() => {
        // Create player inside container.
        if (playerContainerRef.current) {
            const config: UIPlayerConfiguration = {
                license: THEO_LICENSE_KEY,
                mutedAutoplay: "all",
                allowNativeFullscreen: true,
            };
            videoRef.current = new Player(playerContainerRef.current, config);
        }

        // Clean up
        return () => {
            if (videoRef.current) {
                videoRef.current.destroy();
            }
        };
    }, []);
    React.useEffect(() => {
        // Create player inside container.
        if (playerContainerBlurRef.current) {
            const config: UIPlayerConfiguration = {
                license: THEO_LICENSE_KEY,
                mutedAutoplay: "all",
                allowNativeFullscreen: true,
            };
            videoBlurRef.current = new Player(
                playerContainerBlurRef.current,
                config,
            );
        }

        // Clean up
        return () => {
            if (videoBlurRef.current) {
                videoBlurRef.current.destroy();
                videoBlurRef.current = null;
            }
        };
    }, [blur]);

    React.useEffect(() => {
        if (!videoRef.current) {
            return;
        }
        videoRef.current.autoplay = autoplay as boolean;
        videoRef.current.source = {
            sources: [
                {
                    src: videoUrl,
                    crossOrigin: "anonymous",
                },
            ],
        };
        videoRef.current.poster = thumbnailUrl || "";
    }, [videoUrl]);
    React.useEffect(() => {
        if (videoBlurRef.current) {
            videoBlurRef.current.muted = true;
            videoBlurRef.current.autoplay = false;
            videoBlurRef.current.source = {
                sources: [
                    {
                        src: videoUrl,
                        crossOrigin: "anonymous",
                    },
                ],
            };
            videoBlurRef.current.poster = thumbnailUrl || "";
        }
    }, [videoBlurRef.current]);

    const rewind = (play = false) => {
        const video = videoRef.current;
        const videoBlur = videoBlurRef.current;
        if (!video) {
            return;
        }
        video.currentTime = offsetStart;
        if (videoBlur) {
            videoBlur.currentTime = offsetStart;
        }
        setCurrentTime(offsetStart);
        if (play) {
            video.play();
            if (videoBlur) {
                videoBlur.play();
            }
            return;
        }
        video.pause();
        if (videoBlur) {
            videoBlur.pause();
        }
    };

    const playLastSeconds = (count = 2) => {
        const video = videoRef.current;
        const videoBlur = videoBlurRef.current;

        if (!video) {
            return;
        }
        const videoOffset = video.duration - offsetEnd;
        let startSecond = videoOffset - count;
        if (startSecond < offsetStart) {
            startSecond = offsetStart;
        }
        if (startSecond === video.duration) {
            // "Fix" for 'Go to out point' button
            // Scrubbing to end of video will cause the player to stop and go to first frame
            startSecond = video.duration - 0.01;
        }

        setCurrentTime(startSecond);
        video.currentTime = startSecond;
        video.play();
        if (videoBlur) {
            videoBlur.currentTime = startSecond;
            videoBlur.play();
        }
    };

    const moveFrame = (direction: "forward" | "backward") => {
        const video = videoRef.current;
        const videoBlur = videoBlurRef.current;
        const framerate = DEFAULT_FRAME_RATE;
        if (!video) {
            return;
        }
        video.pause();
        if (videoBlur) {
            videoBlur.pause();
        }
        if (direction === "forward") {
            const newTime = video.currentTime + 1 / +framerate;
            if (newTime > video.duration - offsetEnd) {
                let newOffsetEnd = video.duration - newTime;
                if (newOffsetEnd < 0) {
                    newOffsetEnd = 0;
                }
                setOffsetEnd(newOffsetEnd);
            }
            video.currentTime = newTime;
            if (videoBlur) {
                videoBlur.currentTime = newTime;
            }
        } else {
            let newTime = video.currentTime - 1 / +framerate;
            if (newTime < offsetStart) {
                if (newTime < 0) {
                    newTime = 0;
                }
                setOffsetStart(newTime);
            }
            video.currentTime = newTime;
            if (videoBlur) {
                videoBlur.currentTime = newTime;
            }
        }
    };

    const markIn = () => {
        setOffsetStart(currentTime);
    };

    const markOut = () => {
        setOffsetEnd(videoDuration - currentTime);
    };

    const onDraggingStart = () => {
        const videoEl = videoRef.current;
        const videoBlurEl = videoBlurRef.current;
        if (videoEl) {
            setWasPlaying(!videoEl.paused);
            if (wasPlaying) {
                videoEl.pause();
                if (videoBlurEl) {
                    videoBlurEl.pause();
                }
            }
        }
    };

    const onDraggingEnd = () => {
        const videoEl = videoRef.current;
        const videoBlurEl = videoBlurRef.current;
        if (videoEl && wasPlaying) {
            videoEl.play();
            if (videoBlurEl) {
                videoBlurEl.play();
            }
        }
    };

    const onDurationChange = (name: "dot" | "start" | "end", value: number) => {
        const videoEl = videoRef.current;
        const videoBlurEl = videoBlurRef.current;
        if (name === "dot") {
            setUserInputId(createUserId());
            setCurrentTime(value);
            if (videoEl) {
                videoEl.currentTime = value;
                if (videoBlurEl) {
                    videoBlurEl.currentTime = value;
                }
            }
        }
        if (name === "start") {
            setUserInputId(createUserId());
            setOffsetStart(value);
            if (videoEl) {
                videoEl.currentTime = value;
                if (videoBlurEl) {
                    videoBlurEl.currentTime = value;
                }
            }
        }
        if (name === "end") {
            setUserInputId(createUserId());
            setOffsetEnd(value);
            if (videoEl) {
                videoEl.currentTime = videoDuration - value;
                if (videoBlurEl) {
                    videoBlurEl.currentTime = videoDuration - value;
                }
            }
        }
    };

    const handlePlay = () => {
        const videoEl = videoRef.current;
        const blurEl = videoBlurRef.current;
        if (!videoEl) {
            return;
        }
        if (videoEl.paused) {
            if (
                normalizeCurrentTime(currentTime) >=
                normalizeCurrentTime(videoDuration - offsetEnd)
            ) {
                videoEl.currentTime = offsetStart;
            }
            videoEl.play();
            if (blurEl) {
                blurEl.play();
            }
            return;
        }
        videoEl.pause();
        if (blurEl) {
            blurEl.pause();
        }
    };

    const changeArea = (changedCrop: Crop) => {
        if (!videoRef.current) {
            return;
        }
        const videoEl = videoRef.current;
        // Unscaled sizes: Frame: 1280/720, video: 1240/720
        // Scaled sizes: Frame: 768/432, square: 432/432
        // Scaled size -> video size
        // Square -> video size -> desired size.
        // => 1240/(1240/1280*768)*432
        const { videoWidth } = videoEl;
        // Fix for BNR
        // TODO: This translation needs to happen in the changedCropming area
        // Step 1): True video size needs to be calculated and constrained
        // Step 2): Translation needs to be applied to 'video square' rather than
        //          the size of the player element itself
        const deltaXOffset = 0;
        const multiplierWidth =
            videoSize.width /
            ((videoSize.width / videoWidth) * videoEl.element.offsetWidth);
        const multiplierHeight =
            videoSize.height /
            ((videoSize.height / videoEl.videoHeight) *
                videoEl.element.offsetHeight);
        const realX =
            Math.floor(changedCrop.point.x * multiplierWidth) - deltaXOffset;
        const realY = Math.floor(changedCrop.point.y * multiplierHeight);
        const realWidth = Math.floor(changedCrop.size.width * multiplierWidth);
        const realHeight = Math.floor(
            changedCrop.size.height * multiplierHeight,
        );
        const outputSize = {
            width: Math.ceil(changedCrop.outputSize.width * multiplierWidth),
            height: Math.ceil(changedCrop.outputSize.height * multiplierHeight),
        };
        const newCrop = new Crop();
        newCrop.size = { width: realWidth, height: realHeight };
        newCrop.point = { x: realX, y: realY };
        newCrop.outputSize = outputSize;
        newCrop.length = videoEl.duration ? videoEl.duration : 0.0;

        // Last pass to ensure correctness of the crop
        newCrop.length = Number.parseFloat(newCrop.length.toFixed(2));
        newCrop.size.width -= newCrop.size.width % 2;
        newCrop.size.height -= newCrop.size.height % 2;
        newCrop.outputSize.width -= newCrop.outputSize.width % 2;
        newCrop.outputSize.height -= newCrop.outputSize.height % 2;
        setCrop(newCrop);
        setDynamicVideoStyle({});
        dynamicDurationStyle = {};
    };

    const handleResizeArea = (trim: Crop) => {
        if (sizeMode === "resize") {
            setDynamicVideoStyle({
                width: `${trim.size.width}px`,
                marginLeft: `${trim.point.x}px`,
            });
            const leftOffset = trim.size.width + trim.point.x - 60; // 60 = duration element width + margin
            dynamicDurationStyle = {
                left: `${leftOffset}px`,
                right: "auto",
            };
        }
    };

    const handleButtonClicked = (buttonType: PlayerButtonType): void => {
        switch (buttonType) {
            case "goToInPoint":
                rewind();
                break;
            case "goToOutPoint":
                playLastSeconds(0);
                break;
            case "moveFrameBackward":
                moveFrame("backward");
                break;
            case "moveFrameForward":
                moveFrame("forward");
                break;
            case "playFromStart":
                rewind(true);
                break;
            case "playLast2Seconds":
                playLastSeconds(2);
                break;
            case "setInPoint":
                markIn();
                break;
            case "setOutPoint":
                markOut();
                break;
            case "togglePlay":
                handlePlay();
                break;
            default:
                break;
        }
    };

    React.useEffect(() => {
        const videoEl = videoRef.current;
        if (!videoEl) {
            return;
        }
        videoEl.currentTime = currentTime;
    }, [userInputId]);

    React.useEffect(() => {
        onChange(offsetStart, offsetEnd, crop);
    }, [offsetStart, offsetEnd, crop, onChange]);

    React.useEffect(() => {
        const video = videoRef.current;
        const videoBlur = videoBlurRef.current;
        if (!video) {
            return undefined;
        }
        const checkVideoStatus = () => {
            if (
                normalizeCurrentTime(video.currentTime) <
                normalizeCurrentTime(offsetStart)
            ) {
                video.currentTime = offsetStart;
                if (videoBlur) {
                    videoBlur.currentTime = offsetStart;
                }
            } else if (
                normalizeCurrentTime(video.currentTime) >
                normalizeCurrentTime(video.duration - offsetEnd)
            ) {
                if (video.paused === false) {
                    video.pause();
                    if (videoBlur) {
                        videoBlur.pause();
                    }
                } else {
                    video.currentTime = video.duration - offsetEnd;
                    if (videoBlur) {
                        videoBlur.currentTime = video.duration - offsetEnd;
                    }
                }
            }
        };
        checkVideoStatus();

        const onPlay = (): void => {
            setPlaying(true);
            if (videoBlur) {
                videoBlur.play();
            }
        };
        const onPause = (): void => {
            setPlaying(false);
            if (videoBlur) {
                videoBlur.pause();
            }
        };
        const onTimeUpdate = (): void => {
            console.log("onTimeUpdate called", video.currentTime);
            setCurrentTime(video.currentTime);
            checkVideoStatus();
        };
        const onLoadedMetadata = (): void => {
            setVideoDuration(video.duration);
            setVideoSize({
                width: video.videoWidth,
                height: video.videoHeight,
            });
            setPlaying(false);
            video.pause();
        };

        video.addEventListener(["play", "playing"], onPlay);
        video.addEventListener("pause", onPause);
        video.addEventListener("timeupdate", onTimeUpdate);
        video.addEventListener("loadedmetadata", onLoadedMetadata);

        return () => {
            if (!video) {
                return;
            }
            video.removeEventListener(["play", "playing"], onPlay);
            video.removeEventListener("pause", onPause);
            video.removeEventListener("timeupdate", onTimeUpdate);
            video.removeEventListener("loadedmetadata", onLoadedMetadata);
            if (animationFrame.current) {
                cancelAnimationFrame(animationFrame.current);
            }
        };
    }, [videoRef.current]);

    React.useEffect(() => {
        if (!trimMode) {
            setDynamicVideoStyle({});
            dynamicDurationStyle = {};
        }
    }, [trimMode]);

    return (
        <div className={$.container}>
            <div className={$.container__inner}>
                <div className={$.container__gradient} />
                <div
                    className={$.container__video__duration}
                    style={dynamicDurationStyle}
                >
                    {formatDuration(videoDuration - offsetEnd - offsetStart)}
                </div>
                <div
                    style={{ opacity: videoDuration === 0 ? 1.0 : 0.0 }}
                    className={$.container__overlay}
                >
                    <span className={$.container__overlay__spinner}>
                        <Spinner />
                    </span>
                    <span>Loading video…</span>
                </div>
                {blur && (
                    <div
                        ref={playerContainerBlurRef}
                        className={cx(
                            $.container__video,
                            $.container__video__blur,
                        )}
                    />
                )}
                <div
                    ref={playerContainerRef}
                    style={dynamicVideoStyle}
                    className={$.container__video}
                />

                {trimMode && sizeMode === "crop" && (
                    <TrimmingArea
                        aspectRatio={trimMode}
                        onChange={changeArea}
                    />
                )}
                {trimMode && sizeMode === "resize" && (
                    <ResizeArea
                        trimArea={handleResizeArea}
                        aspectRatio={trimMode}
                    />
                )}
            </div>
            <DurationBarV2
                className={$.container__durationBar}
                progress={currentTime}
                max={videoDuration}
                startOffset={offsetStart}
                endOffset={offsetEnd}
                vidRef={videoRef}
                onDraggingStart={onDraggingStart}
                onDraggingEnd={onDraggingEnd}
                onChange={onDurationChange}
            />
            <PlayerButtons
                trimButtons
                useShortcuts
                playing={playing}
                onButtonClick={handleButtonClicked}
            />
        </div>
    );
};
export default TrimPlayerV2;
